
import { createContext, useContext, useState } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface CommentsContextType {
  comments: any[];
  isSubmitting: boolean;
  replyTo: { id: string; username: string } | null;
  handleSubmitComment: (pinId: string, newComment: string) => Promise<void>;
  handleSubmitReply: (pinId: string, replyComment: string) => Promise<void>;
  setReplyTo: (reply: { id: string; username: string } | null) => void;
  onCommentAdded: () => void;
}

const CommentsContext = createContext<CommentsContextType | undefined>(undefined);

export function CommentsProvider({ 
  children,
  initialComments = [],
  onCommentAdded
}: { 
  children: React.ReactNode;
  initialComments: any[];
  onCommentAdded: () => void;
}) {
  const [comments, setComments] = useState(initialComments);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [replyTo, setReplyTo] = useState<{ id: string; username: string } | null>(null);

  const handleSubmitComment = async (pinId: string, newComment: string) => {
    if (!newComment.trim()) return;

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      toast.error("Você precisa estar logado para comentar");
      return;
    }

    setIsSubmitting(true);
    try {
      const { error } = await supabase.from("pin_comments").insert({
        pin_id: pinId,
        user_id: session.user.id,
        content: newComment.trim()
      });

      if (error) throw error;
      
      onCommentAdded();
      toast.success("Comentário adicionado com sucesso!");
    } catch (error) {
      console.error("Error adding comment:", error);
      toast.error("Erro ao adicionar comentário");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitReply = async (pinId: string, replyComment: string) => {
    if (!replyComment.trim() || !replyTo) return;

    setIsSubmitting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error("Você precisa estar logado para comentar");
        return;
      }

      const { error } = await supabase.from("pin_comments").insert({
        pin_id: pinId,
        user_id: session.user.id,
        content: replyComment.trim(),
        parent_id: replyTo.id
      });

      if (error) throw error;
      
      setReplyTo(null);
      onCommentAdded();
      toast.success("Resposta adicionada com sucesso!");
    } catch (error) {
      console.error("Error adding reply:", error);
      toast.error("Erro ao adicionar resposta");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <CommentsContext.Provider 
      value={{ 
        comments, 
        isSubmitting, 
        replyTo,
        setReplyTo,
        handleSubmitComment,
        handleSubmitReply,
        onCommentAdded
      }}
    >
      {children}
    </CommentsContext.Provider>
  );
}

export const useComments = () => {
  const context = useContext(CommentsContext);
  if (context === undefined) {
    throw new Error('useComments must be used within a CommentsProvider');
  }
  return context;
};
